import { DemoOrangeIllustration } from '@carboncopyco/illustrations';
import {
  Button,
  Checkbox,
  EmptyView,
  Paper,
  TextBase,
  TooltipBubble,
  UserBasicInfo,
} from '@carboncopyco/ui';
import { MMMDoYYYY } from '@carboncopyco/utils';
import { type ReactNode, useMemo } from 'react';
import { type Col, FixedTable } from '../../FixedTable';
import { usePlaybookModalState } from '../PlaybookModalContext';
import {
  PLAYBOOK_ASSET_LIMIT,
  iconByAssetType,
  iconTitleByAssetType,
  titleKeyByAssetType,
} from '../consts';
import type { PlaybookSearchResult } from '../types';
import { AssetLocationBreadcrumbs } from './AssetLocationBreadcrumbs';
import { AssetSelectionControls } from './AssetSelectionControls';
import { AssetSelectionSkeleton } from './AssetSelectionSkeleton';
import { PanelRowWrapper } from './PanelRowWrapper';

const getPlaybookAssetTableCols = ({
  checked,
  maxAssetsSelected,
}: {
  checked: Record<string, boolean>;
  maxAssetsSelected: boolean;
}): Col<PlaybookSearchResult>[] => [
  {
    alignment: 'left',
    title: '',
    width: 40,
    render: (item) => {
      return (
        <div className="flex items-center">
          <Checkbox
            disabled={!checked[item.id] && maxAssetsSelected}
            name={item[titleKeyByAssetType[item.entityType]]}
            checked={checked[item.id]}
            onChange={() => undefined}
          />
        </div>
      );
    },
  },
  {
    alignment: 'left',
    title: 'Asset name',
    width: 208,
    maxContentWidth: 188,
    render: (item) => (
      <div className="flex items-center gap-2.5">
        <TooltipBubble
          title={iconTitleByAssetType[item.entityType]}
          placement="top"
        >
          <div className="shrink-0">{iconByAssetType[item.entityType]}</div>
        </TooltipBubble>
        <TooltipBubble
          placement="top"
          title={item[titleKeyByAssetType[item.entityType]]}
        >
          <div className="truncate max-w-[160px]">
            <TextBase
              label={item[titleKeyByAssetType[item.entityType]]}
              color="text-ds-natural-600"
            />
          </div>
        </TooltipBubble>
      </div>
    ),
  },
  {
    alignment: 'left',
    title: 'Created by',
    width: 172,
    maxContentWidth: 155,
    render: (item) => (
      <UserBasicInfo
        size="small"
        user={item?.creator}
        textColor="text-ds-natural-600"
        truncateWidth={130}
        tooltip
      />
    ),
  },
  {
    alignment: 'left',
    title: 'Last edited',
    width: 140,
    render: (item) => (
      <TooltipBubble
        title={MMMDoYYYY(item.updateTime)}
        placement="top-start"
      >
        <TextBase label={MMMDoYYYY(item.updateTime)} />
      </TooltipBubble>
    ),
  },
  {
    alignment: 'left',
    title: 'Location',
    width: 192,
    maxContentWidth: 192,
    render: (item) => {
      const isInRoot = item.folderId === '0';
      return (
        <AssetLocationBreadcrumbs
          workspaceId={item.workspaceId}
          organizationId={item.organizationId}
          location={{
            id: isInRoot ? item.workspaceId : String(item.folderId),
            entity: isInRoot ? 'workspace' : 'folder',
          }}
        />
      );
    },
  },
];

const AssetSelectionPanelWrapper = ({ children }: { children: ReactNode }) => (
  <Paper
    radius
    border="ds-regular"
  >
    <div className="w-[820px] px-2 py-5">{children}</div>
  </Paper>
);

export const AssetSelectionPanel = () => {
  const {
    items,
    error,
    isLoading,
    isLoadingBatch,
    fetchNextBatch,
    assets,
    creators,
    types,
    query,
    setCreators,
    setTypes,
    setQuery,
    isEmpty,
  } = usePlaybookModalState();

  const checked = Object.fromEntries(
    assets.map((asset) => [asset.assetId, true])
  );
  const maxAssetsSelected = assets.length >= PLAYBOOK_ASSET_LIMIT;

  const cols = useMemo(
    () =>
      getPlaybookAssetTableCols({
        checked,
        maxAssetsSelected,
      }),
    [checked, maxAssetsSelected]
  );
  const enhancedItems = (items || []).map((item) => ({
    ...item,
    isSelected: checked[item.id],
  }));

  const isDirty =
    Object.entries(creators).filter(([, val]) => val).length ||
    Object.entries(types).filter(([, val]) => val).length ||
    query;

  const resetControls = () => {
    setCreators({});
    setTypes({});
    setQuery('');
  };

  if (isEmpty && !isDirty) {
    return (
      <AssetSelectionPanelWrapper>
        <EmptyView
          illustration={<DemoOrangeIllustration />}
          title="No items yet"
          message={
            <div className="flex flex-col items-center gap-5 ">
              <TextBase label="Once you'll create an item, it will appear here" />
            </div>
          }
        />
      </AssetSelectionPanelWrapper>
    );
  }

  return (
    <AssetSelectionPanelWrapper>
      <AssetSelectionControls />
      {isEmpty && isDirty ? (
        <div className="h-[420px]">
          <EmptyView
            title="No results"
            message={
              <div className="flex flex-col items-center gap-5 ">
                <TextBase label="Nothing matches your search" />
                <Button
                  link
                  label="Clear filters"
                  onClick={resetControls}
                />
              </div>
            }
          />
        </div>
      ) : (
        <FixedTable<PlaybookSearchResult & { isSelected: boolean }>
          infiniteListMaxHeight={380}
          error={error}
          headerRowClassName="px-3 pt-3"
          data={enhancedItems}
          cols={cols}
          loader={<AssetSelectionSkeleton count={6} />}
          isLoading={isLoading}
          isLoadingBatch={isLoadingBatch}
          handleFetchNextBatch={fetchNextBatch}
          rowWrapper={PanelRowWrapper}
        />
      )}
    </AssetSelectionPanelWrapper>
  );
};
